import { companyReportUrls } from '@app/ccf/company-report/report/company-report-utils';
import EmissionsByCategory from '@app/components/reports/sections/food-and-drink/emissions-by-category';
import FoodAndDrink from '@app/components/reports/sections/food-and-drink/food-and-drink';
import FoodItems from '@app/components/reports/sections/food-and-drink/food-items';
import EmissionsByMaterial from '@app/components/reports/sections/packaging/emissions-by-material';
import Packaging from '@app/components/reports/sections/packaging/packaging';
import PackagingItems from '@app/components/reports/sections/packaging/packaging-items';
import EmissionsBySite from '@app/components/reports/sections/result-overview/emissions-by-site';
import EmissionsBySupplier from '@app/components/reports/sections/result-overview/emissions-by-supplier';
import EmissionsByTag from '@app/components/reports/sections/result-overview/emissions-by-tag';
import EmissionsOverTime from '@app/components/reports/sections/result-overview/emissions-over-time';
import ResultsOverview from '@app/components/reports/sections/result-overview/results-overview';
import {
  ReportFilters,
  scrollTo,
  useReportFilterParams,
} from '@app/components/reports/utils';
import {
  OperationReportTypeEnum,
  PurchaseReportResultData,
} from '@shared/api/types';
import { generatePath } from 'react-router-dom';
import { ReportSection } from '../../components/reports/types';
import { purchasedGoodsRoutes } from '../purchased-goods-routes';
import { purchaseReportSlugs } from '../slugs';
import { purchaseReportUrls } from './purchase-report-utils';

export function usePurchaseReportSections(
  result: PurchaseReportResultData,
  reportUuid?: string
): ReportSection[] {
  const filterParams = useReportFilterParams(
    result?.filters as ReportFilters[]
  );
  if (!reportUuid || !result) return [];

  // Append url params to purchased items links
  const purchasedFoodItemsLink = `${generatePath(
    purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK
  )}?${filterParams.toString()}`;

  const purchasedPackagingItemsLink = `${generatePath(
    purchasedGoodsRoutes.PURCHASED_PACKAGING
  )}?${filterParams.toString()}`;

  return [
    {
      title: 'Results overview',
      preTitle: '',
      slug: purchaseReportSlugs.resultOverview,
      url: purchaseReportUrls.resultOverview,
      onClick: () => scrollTo(companyReportUrls.introduction),
      component: (
        <ResultsOverview reportType={OperationReportTypeEnum.purchase} />
      ),
      show: true,
      items: [
        {
          title: 'Emissions over time',
          preTitle: 'Results overview',
          slug: purchaseReportSlugs.emissionsOverTime,
          url: purchaseReportUrls.emissionsOverTime,
          onClick: () => scrollTo(purchaseReportUrls.emissionsOverTime),
          component: (
            <EmissionsOverTime reportType={OperationReportTypeEnum.purchase} />
          ),
          show: true,
        },
        {
          title: 'Emissions by site',
          preTitle: 'Results overview',
          slug: purchaseReportSlugs.emissionsBySite,
          url: purchaseReportUrls.emissionsBySite,
          onClick: () => scrollTo(purchaseReportUrls.emissionsBySite),
          component: (
            <EmissionsBySite reportType={OperationReportTypeEnum.purchase} />
          ),
          show: true,
        },
        {
          title: 'Emissions by supplier',
          preTitle: 'Results overview',
          slug: purchaseReportSlugs.emissionsBySupplier,
          url: purchaseReportUrls.emissionsBySupplier,
          onClick: () => scrollTo(purchaseReportUrls.emissionsBySupplier),
          component: (
            <EmissionsBySupplier
              reportType={OperationReportTypeEnum.purchase}
            />
          ),
          show: true,
        },
        {
          title: 'Emissions by tag',
          preTitle: 'Results overview',
          slug: purchaseReportSlugs.emissionsByTag,
          url: purchaseReportUrls.emissionsByTag,
          onClick: () => scrollTo(purchaseReportUrls.emissionsByTag),
          component: (
            <EmissionsByTag reportType={OperationReportTypeEnum.purchase} />
          ),
          show: true,
        },
      ],
    },
    {
      title: 'Food and drink',
      preTitle: '',
      url: purchaseReportUrls.foodAndDrink,
      slug: purchaseReportSlugs.foodAndDrink,
      onClick: () => scrollTo(purchaseReportUrls.foodAndDrink),
      component: <FoodAndDrink reportType={OperationReportTypeEnum.purchase} />,
      show: true,
      items: [
        {
          title: 'Emissions by category',
          preTitle: 'Food and drink',
          slug: purchaseReportSlugs.emissionsByCategory,
          url: purchaseReportUrls.emissionsByCategory,
          onClick: () => scrollTo(purchaseReportUrls.emissionsByCategory),
          component: (
            <EmissionsByCategory
              reportType={OperationReportTypeEnum.purchase}
            />
          ),
          show: true,
        },
        {
          title: 'Purchased items',
          preTitle: 'Food and drink',
          slug: purchaseReportSlugs.purchasedItemsFood,
          url: purchaseReportUrls.purchasedItemsFood,
          onClick: () => scrollTo(purchaseReportUrls.purchasedItemsFood),
          component: (
            <FoodItems reportType={OperationReportTypeEnum.purchase} />
          ),
          show: true,
          link: {
            title: 'View all items',
            linkTo: generatePath(purchasedFoodItemsLink),
          },
        },
      ],
    },
    {
      title: 'Packaging',
      preTitle: '',
      url: purchaseReportUrls.packaging,
      slug: purchaseReportSlugs.packaging,
      onClick: () => scrollTo(purchaseReportUrls.packaging),
      component: <Packaging reportType={OperationReportTypeEnum.purchase} />,
      show: true,
      items: [
        {
          title: 'Emissions by material',
          preTitle: 'Packaging',
          url: purchaseReportUrls.emissionsByMaterial,
          slug: purchaseReportSlugs.emissionsByMaterial,
          onClick: () => scrollTo(purchaseReportUrls.emissionsByMaterial),
          component: (
            <EmissionsByMaterial
              reportType={OperationReportTypeEnum.purchase}
            />
          ),
          show: true,
        },
        {
          title: 'Purchased items',
          preTitle: 'Packaging',
          url: purchaseReportUrls.purchasedItemsPackaging,
          slug: purchaseReportSlugs.purchasedItemsPackaging,
          onClick: () => scrollTo(purchaseReportUrls.purchasedItemsPackaging),
          component: (
            <PackagingItems reportType={OperationReportTypeEnum.purchase} />
          ),
          show: true,
          link: {
            title: 'View all items',
            linkTo: generatePath(purchasedPackagingItemsLink),
          },
        },
      ],
    },
  ];
}
